// src/pages/CollectionPage.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, SlidersHorizontal, X, Check } from "lucide-react";
import ProductCard from "../components/common/ProductCard";

const SORT_OPTIONS = [
  { id:"featured",     label:"Featured" },
  { id:"relevant",     label:"Most relevant" },
  { id:"best_selling", label:"Best selling" },
  { id:"az",           label:"Alphabetically, A-Z" },
  { id:"za",           label:"Alphabetically, Z-A" },
  { id:"price_asc",    label:"Price, low to high" },
  { id:"price_desc",   label:"Price, high to low" },
  { id:"date_old",     label:"Date, old to new" },
  { id:"date_new",     label:"Date, new to old" },
];

const FILTER_SECTIONS = [
  { id:"availability", label:"AVAILABILITY",  options:["In stock","Out of stock"] },
  { id:"product_type", label:"PRODUCT TYPE",  options:["Daily Planner","Weekly Planner","Monthly Planner","Journal","Notebook"] },
  { id:"color",        label:"COLOR",          options:["Black","White","Blue","Green","Red","Yellow","Purple","Pink","Orange","Brown"] },
  { id:"style",        label:"STYLE",          options:["Minimal","Floral","Typography","Abstract","Pattern","Monogram","Bold"] },
  { id:"add_photo",    label:"ADD A PHOTO",    options:["With Photo","Without Photo"] },
];

function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
      .og-display{font-family:'Fraunces',serif}
      .og-body{font-family:'DM Sans',sans-serif}
    `}</style>
  );
}

function Stars({ value }) {
  const v = Math.round(parseFloat(value));
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={13} className={n <= v ? "fill-black text-black" : "fill-none text-black/30"}/>
      ))}
    </div>
  );
}

function applySort(list, sort) {
  const copy = [...list];
  switch(sort) {
    case "featured":   return copy.sort((a,b) => Number(b.featured)-Number(a.featured));
    case "az":         return copy.sort((a,b) => a.name.localeCompare(b.name));
    case "za":         return copy.sort((a,b) => b.name.localeCompare(a.name));
    case "price_asc":  return copy.sort((a,b) => Number(a.price)-Number(b.price));
    case "price_desc": return copy.sort((a,b) => Number(b.price)-Number(a.price));
    case "date_old":   return copy.sort((a,b) => new Date(a.date||0)-new Date(b.date||0));
    case "date_new":   return copy.sort((a,b) => new Date(b.date||0)-new Date(a.date||0));
    default:           return copy;
  }
}

// ── Sort Dropdown ─────────────────────────────────────────────────────────────
function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(o => o.id === value);
  return (
    <div className="relative" style={{ zIndex: 100 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-[13px] sm:text-sm font-medium text-gray-700 hover:text-black select-none whitespace-nowrap"
      >
        <span className="text-gray-500 font-normal">Sort by:</span>
        <span className="font-semibold">{current?.label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M18 15l-6-6-6 6"/>
        </svg>
      </button>

      {open && (
        <>
          {/* Overlay — mobile pe bottom-sheet ke peeche dark backdrop bhi lagega */}
          <div className="fixed inset-0 bg-black/20 sm:bg-transparent" style={{ zIndex: 98 }} onClick={() => setOpen(false)}/>

          {/* Mobile: bottom-sheet (screen ke neeche se slide-up, full width).
              Desktop (sm+): pehle jaisa hi small absolute dropdown, button ke right-aligned. */}
          <div
            className="fixed left-0 right-0 bottom-0 rounded-t-2xl sm:absolute sm:left-auto sm:right-0 sm:bottom-auto sm:top-full sm:mt-3 sm:rounded-xl bg-white border-t sm:border border-gray-200 shadow-2xl overflow-hidden sm:min-w-[220px] sm:max-w-[calc(100vw-2rem)] max-h-[70vh] sm:max-h-none overflow-y-auto"
            style={{ zIndex: 99 }}
          >
            {/* Mobile-only header with title + close, taaki bottom-sheet clearly usable lage */}
            <div className="flex sm:hidden items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="text-sm font-bold">Sort by</span>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {SORT_OPTIONS.map(opt => (  
              <button
                key={opt.id}
                type="button"
                onClick={() => { onChange(opt.id); setOpen(false); }}
                className="w-full flex items-center justify-between px-5 sm:px-4 py-3.5 sm:py-2.5 text-sm hover:bg-gray-50 transition-colors text-left gap-4"
              >
                <span className={value === opt.id ? "font-semibold" : ""}>{opt.label}</span>
                {value === opt.id && <Check size={13} className="text-black flex-shrink-0"/>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Filter Panel ──────────────────────────────────────────────────────────────
function FilterPanel({ open, onClose, filters, onFilterChange, totalResults }) {
  const [openSections, setOpenSections] = useState(
    Object.fromEntries(FILTER_SECTIONS.map(s => [s.id, true]))
  );
  const toggleSection = id => setOpenSections(p => ({ ...p, [id]: !p[id] }));
  const toggleOption = (sectionId, opt) => {
    const cur = filters[sectionId] || [];
    onFilterChange(sectionId, cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt]);
  };
  const totalActive = Object.values(filters).flat().length;

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/20 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        style={{ zIndex: 200 }}
      />
      <div
        className={`fixed left-0 top-0 h-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out og-body ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{ width: 340, maxWidth: "88vw", zIndex: 201 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-bold">Filters</span>
            {totalActive > 0 && (
              <span className="w-5 h-5 rounded-full bg-black text-white text-[10px] font-bold flex items-center justify-center">
                {totalActive}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
            <X size={18} className="text-gray-600"/>
          </button>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto">
          {FILTER_SECTIONS.map(section => (
            <div key={section.id} className="border-b border-gray-100">
              <button type="button"
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-5 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors">
                <span className="text-xs font-bold tracking-wider text-gray-700">{section.label}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                  className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${openSections[section.id] ? "rotate-180" : ""}`}>
                  <path d="M18 15l-6-6-6 6"/>
                </svg>
              </button>
              {openSections[section.id] && (
                <div className="px-5 sm:px-6 pb-4 flex flex-col gap-3">
                  {section.options.map(opt => {
                    const checked = (filters[section.id] || []).includes(opt);
                    return (
                      <label key={opt} className="flex items-center gap-3 cursor-pointer group select-none">
                        <div
                          onClick={() => toggleOption(section.id, opt)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            checked ? "bg-black border-black" : "border-gray-300 group-hover:border-gray-500"
                          }`}>
                          {checked && <Check size={11} className="text-white" strokeWidth={3}/>}
                        </div>
                        <span className="text-sm text-gray-700 group-hover:text-black transition-colors">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 sm:px-6 py-4 flex gap-3 flex-shrink-0">
          {totalActive > 0 && (
            <button type="button"
              onClick={() => onFilterChange("__clear__", [])}
              className="flex-1 py-3 text-sm font-semibold border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
              Clear all
            </button>
          )}
          <button type="button" onClick={onClose}
            className="flex-1 py-3 text-sm font-bold bg-black text-white rounded-xl hover:opacity-90 transition-opacity">
            View results ({totalResults})
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CollectionPage({ config }) {
  const {
    products = [],
    basePath = "/dailyplanners",
    announcement,
    hero = {},
  } = config;

  const [sort, setSort]             = useState("featured");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters]       = useState({});

  const handleFilterChange = (sectionId, values) => {
    if (sectionId === "__clear__") { setFilters({}); return; }
    setFilters(p => ({ ...p, [sectionId]: values }));
  };

  // Pagination hata di gayi — ab sorted list ke saare products ek hi page pe dikhte hain
  const sorted      = useMemo(() => applySort(products, sort), [products, sort]);
  const totalActive = Object.values(filters).flat().length;

  const handleSortChange = v => setSort(v);

  const hasRightCol = hero.quote || hero.rating || hero.featuredIn;

  return (
    <div className="og-body min-h-screen bg-white text-black">
      <FontImport/>

      <FilterPanel
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        totalResults={sorted.length}
      />

      {announcement && (
        <div className="bg-[#FFEB3B] px-4 py-2.5 sm:py-3 text-center">
          <p className="og-display text-[13.5px] italic sm:text-[15px] md:text-lg font-medium text-black">{announcement.headline}</p>
          {announcement.subtext && <p className="text-[11px] sm:text-[12px] text-black/80 font-medium mt-0.5">{announcement.subtext}</p>}
        </div>
      )}

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pb-7 sm:pb-10 pt-8 sm:pt-12 sm:px-6">
        <div className={`grid gap-5 sm:gap-8 items-center ${hasRightCol ? "md:grid-cols-[1.3fr_1fr]" : ""}`}>
          <div>
            <h1 className="og-display text-[34px] sm:text-5xl italic leading-[1.05] sm:leading-none md:text-6xl text-black">{hero.title}</h1>
            {hero.subtitle && <p className="mt-3 sm:mt-4 text-gray-600 text-[14px] sm:text-[15px]">{hero.subtitle}</p>}
          </div>
          {hasRightCol && (
            <div className="flex flex-col gap-3 sm:gap-4 border-t border-gray-200 pt-5 sm:pt-6 md:border-l md:border-t-0 md:pl-10 md:pt-0">
              {hero.featuredIn && (
                <>
                  <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">As featured in:</p>
                  <div className="flex gap-3 sm:gap-4 items-center opacity-70 mb-2 flex-wrap">
                    {hero.featuredIn.map(pub => <span key={pub} className="font-bold text-sm">{pub}</span>)}
                  </div>
                </>
              )}
              {hero.quote && (
                <>
                  <p className="text-[14px] sm:text-[15px] leading-relaxed text-black">"{hero.quote}"</p>
                  {hero.quoteLink && (
                    <Link to={hero.quoteLink} className="text-sm font-semibold underline underline-offset-4 mb-4 text-black">
                      See customer reviews
                    </Link>
                  )}
                </>
              )}
              {hero.rating && (
                <div className="flex items-center gap-4">
                  <p className="og-display text-3xl sm:text-4xl italic">{hero.rating}/5</p>
                  <div>
                    <Stars value={hero.rating}/>
                    <p className="mt-1 text-[11px] text-gray-500 underline underline-offset-2">
                      from over {hero.ratingCount||"1000+"} reviews
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Toolbar */}
      <section className="mx-auto flex max-w-7xl flex-wrap items-center justify-between border-y border-gray-200 px-4 py-3.5 sm:py-4 sm:px-6 gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-1.5 sm:gap-2 border border-gray-300 rounded-full px-3.5 sm:px-5 py-1.5 sm:py-2 text-[13px] sm:text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <SlidersHorizontal size={14}/>
            All filters
            {totalActive > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black text-white text-[9px] font-bold flex items-center justify-center ">
                {totalActive}
              </span>
            )}
          </button>

          <button type="button"
            className="flex items-center gap-2 border border-gray-300 rounded-full px-3.5 sm:px-5 py-1.5 sm:py-2 text-[13px] sm:text-sm font-medium hover:bg-gray-50 transition-colors">
            Add your text
          </button>

          <p className="text-sm text-gray-500 hidden sm:block">
            Showing {sorted.length} of {products.length} designs
          </p>
        </div>

        <SortDropdown value={sort} onChange={handleSortChange}/>
      </section>

      {/* Active filter chips */}
      {totalActive > 0 && (
        <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 flex flex-wrap gap-2 items-center">
          {Object.entries(filters).map(([sid, vals]) =>
            vals.map(val => (
              <span key={`${sid}-${val}`}
                className="inline-flex items-center gap-1.5 bg-black text-white text-xs font-medium px-3 py-1.5 rounded-full">
                {val}
                <button type="button"
                  onClick={() => handleFilterChange(sid, filters[sid].filter(v => v !== val))}
                  className="hover:opacity-70"><X size={11}/></button>
              </span>
            ))
          )}
          <button type="button"
            onClick={() => handleFilterChange("__clear__", [])}
            className="text-xs font-semibold text-gray-500 hover:text-black underline underline-offset-2 ml-1">
            Clear all
          </button>
        </div>
      )}

      {/* Grid — ab saare products yaha ek sath dikhte hain, koi pagination nahi */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:py-12 sm:px-6">
        <div className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-12 sm:grid-cols-3 lg:grid-cols-4">
          {sorted.map(product => (
            <ProductCard key={product.id} product={product} basePath={basePath}/>
          ))}
        </div>
      </section>
    </div>
  );
}